import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { API_PATH } from 'config';
import { getAccessTokenApi } from 'api/auth';

// Mapa tipo -> ruta del backend. El type "mantencionesDep" se traduce a
// "mantenciones-deprecadas" porque el endpoint usa kebab-case.
const TYPE_TO_PATH = {
  presupuestos: 'presupuestos',
  comprobantes: 'comprobantes',
  mantenciones: 'mantenciones',
  mantencionesDep: 'mantenciones-deprecadas',
};

/**
 * Listado paginado de documentos por tipo.
 *
 * @param {object} params
 * @param {'presupuestos'|'comprobantes'|'mantenciones'|'mantencionesDep'} params.type
 * @param {number} [params.page=1]
 * @param {number} [params.pageSize=25]
 * @param {string} [params.search]
 * @param {number} [params.idClient]
 * @param {string} [params.dateFrom] - YYYY-MM-DD inclusive.
 * @param {string} [params.dateTo] - YYYY-MM-DD inclusive.
 * @returns {{ rows, total, loading, error, refetch }}
 */
export const useDocumentosList = ({
  type,
  page = 1,
  pageSize = 25,
  search,
  idClient,
  dateFrom,
  dateTo,
} = {}) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const path = TYPE_TO_PATH[type];

  const fetchList = useCallback(async () => {
    if (!path) {
      setRows([]);
      setTotal(0);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const token = getAccessTokenApi();
      const params = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
      };
      if (search) params.search = search;
      if (idClient) params.idClient = idClient;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const { data } = await axios.get(`${API_PATH}/documentos/${path}`, {
        params,
        headers: { Authorization: token },
        signal: controller.signal,
      });
      setRows(data.rows ?? []);
      setTotal(data.total ?? 0);
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      setError(err);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [path, page, pageSize, search, idClient, dateFrom, dateTo]);

  useEffect(() => {
    fetchList();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchList]);

  return { rows, total, loading, error, refetch: fetchList };
};

/**
 * Conteo de documentos por tipo. Re-fetchea cuando cambia refreshKey.
 *
 * @param {number} refreshKey - Bump para forzar refresh.
 * @returns {{ counts, loading, error }}
 */
export const useDocumentosCounts = (refreshKey = 0) => {
  const [counts, setCounts] = useState({
    presupuestos: 0,
    comprobantes: 0,
    mantenciones: 0,
    mantencionesDeprecadas: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = getAccessTokenApi();
        const { data } = await axios.get(`${API_PATH}/documentos/counts`, {
          headers: { Authorization: token },
        });
        if (!cancelled) setCounts(data);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  return { counts, loading, error };
};
